import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  Skeleton,
  Alert,
  Chip,
  Drawer,
  IconButton,
} from '@mui/material';
import { Link as RouterLink, useSearchParams } from 'react-router-dom';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useProducts, useInventoryByLocation } from '../../hooks/useProducts';
import { useProductSearch, ProductSearchParams, StockStatus } from '../../hooks/useProductSearch';
import { useStores } from '../../hooks/useStores';
import { getImageUrl } from '../../types/product';
import { useCartStore } from '../../stores/cartStore';
import { palette } from '../../theme/colors';
import { Product } from '../../services/api';
import { ProductSearch } from '../../components/ProductSearch';
import { ProductFilters } from '../../components/ProductFilters';
import { RecentSearches } from '../../components/RecentSearches';
import type { RecentSearch } from '../../hooks/useProductSearch';

interface CategoryPillProps {
  label: string;
  count: number;
  isActive: boolean;
  onClick: () => void;
}

const CategoryPill: React.FC<CategoryPillProps> = ({ label, count, isActive, onClick }) => (
  <Button
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      px: 2,
      py: 1,
      borderRadius: 50,
      border: `1.5px solid ${isActive ? palette.sidebarBg : palette.border}`,
      bgcolor: isActive ? palette.sidebarBg : 'transparent',
      color: isActive ? palette.surface : palette.textPrimary,
      textTransform: 'none',
      fontWeight: 500,
      fontSize: '0.875rem',
      transition: 'all 0.2s ease',
      '&:hover': {
        bgcolor: isActive ? palette.sidebarBg : palette.sky,
        borderColor: palette.sidebarBg,
      },
    }}
  >
    {label}
    <Box
      component="span"
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minWidth: 24,
        height: 24,
        borderRadius: 50,
        bgcolor: isActive ? palette.primary : palette.sky,
        color: isActive ? palette.textOnPrimary : palette.textPrimary,
        fontSize: '0.75rem',
        fontWeight: 700,
      }}
    >
      {count}
    </Box>
  </Button>
);

export const ProductsPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const initialSearch = searchParams.get('search') || '';

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState(initialSearch);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [minPrice, setMinPrice] = useState<number | undefined>();
  const [maxPrice, setMaxPrice] = useState<number | undefined>();
  const [stockStatus, setStockStatus] = useState<StockStatus>(StockStatus.ALL);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [shouldSaveSearch, setShouldSaveSearch] = useState(false);

  const addItem = useCartStore((state) => state.addItem);

  // Build search params
  const searchParamsObj: ProductSearchParams = useMemo(() => {
    const params: ProductSearchParams = {
      isVisibleInStorefront: true,
    };
    if (searchQuery) params.q = searchQuery;
    if (selectedCategory) params.category = selectedCategory;
    if (minPrice !== undefined) params.minPrice = minPrice;
    if (maxPrice !== undefined) params.maxPrice = maxPrice;
    if (stockStatus !== StockStatus.ALL) params.stockStatus = stockStatus;
    // Only save search when explicitly requested (Apply button clicked)
    if (shouldSaveSearch) params.saveSearch = true;
    return params;
  }, [searchQuery, selectedCategory, minPrice, maxPrice, stockStatus, shouldSaveSearch]);

  // Use search hook if filters are active, otherwise use regular products hook
  const hasFilters = !!(searchQuery || selectedCategory || minPrice || maxPrice || stockStatus !== StockStatus.ALL);
  const { data: searchResults, isLoading: searchLoading, error: searchError, isFetched } = useProductSearch(
    hasFilters ? searchParamsObj : undefined
  );

  // Reset shouldSaveSearch after search is done
  if (isFetched && shouldSaveSearch) {
    setShouldSaveSearch(false);
  }
  const { data: allProducts, isLoading: productsLoading, error: productsError } = useProducts();

  // Use search results if filters are active, otherwise use all products
  const products = hasFilters ? searchResults?.data : allProducts;
  const isLoading = hasFilters ? searchLoading : productsLoading;
  const error = hasFilters ? searchError : productsError;

  const { data: stores } = useStores();

  // Fetch inventory for the first available store to show stock status
  // In a real app, you might want to aggregate inventory across all stores
  const firstStoreId = stores?.[0]?.id;
  const { data: inventoryData } = useInventoryByLocation(firstStoreId || '');

  // Create a map of productId -> available stock.
  // The public availability rows are all scoped to `firstStoreId` (the queried location) and
  // only carry the safe fields (productId/available/inStock) — no internal locationId — so the
  // location is taken from the queried store, not from each row.
  const stockByProductId = useMemo(() => {
    const map = new Map<string, { available: number; locationId: string; locationName: string }>();
    if (inventoryData && stores && firstStoreId) {
      const store = stores.find(s => s.id === firstStoreId);
      const locationName = store?.name || 'Unknown Store';
      inventoryData.forEach((inv) => {
        // Only keep the first (or highest) stock entry per product
        if (!map.has(inv.productId) || (map.get(inv.productId)?.available || 0) < inv.available) {
          map.set(inv.productId, {
            available: inv.available,
            locationId: firstStoreId,
            locationName,
          });
        }
      });
    }
    return map;
  }, [inventoryData, stores, firstStoreId]);

  // Get categories with counts from all products (for filter UI)
  const categoriesWithCounts = useMemo(() => {
    const baseProducts = allProducts || [];
    if (!baseProducts.length) return [];
    const counts: Record<string, number> = {};
    baseProducts.forEach((p) => {
      if (p.category) {
        counts[p.category] = (counts[p.category] || 0) + 1;
      }
    });
    return Object.entries(counts)
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count);
  }, [allProducts]);

  // Extract unique categories for filters
  const uniqueCategories = useMemo(() => {
    return categoriesWithCounts.map(cat => cat.name);
  }, [categoriesWithCounts]);

  const handleAddToCart = (product: Product) => {
    // Get stock info - prefer inventory location, fall back to product.locationId
    const stockInfo = stockByProductId.get(product.id);
    const locationId = stockInfo?.locationId || product.locationId || '';
    const locationName = stockInfo?.locationName || stores?.find(s => s.id === product.locationId)?.name || 'Unknown Store';
    const outOfStock = isProductOutOfStock(product.id);

    addItem({
      productId: product.id,
      name: product.name,
      price: typeof product.price === 'string' ? parseFloat(product.price) : (product.price ?? 0),
      quantity: 1,
      locationId,
      locationName,
      image: product.images?.[0]?.thumbnail,
      organizationId: product.organizationId,
      isPreorder: outOfStock && product.isPreorderable,
    });
  };

  // Helper to check if product is out of stock
  const isProductOutOfStock = (productId: string): boolean => {
    const stockInfo = stockByProductId.get(productId);
    return !stockInfo || stockInfo.available <= 0;
  };

  // Helper to get available stock count
  const getAvailableStock = (productId: string): number => {
    return stockByProductId.get(productId)?.available || 0;
  };

  // Handler for recent search selection
  const handleRecentSearchSelect = (search: RecentSearch) => {
    // Apply search query
    if (search.searchQuery) {
      setSearchQuery(search.searchQuery);
    } else {
      setSearchQuery('');
    }

    // Apply filters from JSONB
    if (search.filters) {
      if (search.filters.category) {
        setSelectedCategory(search.filters.category);
      } else {
        setSelectedCategory('');
      }
      if (search.filters.minPrice !== undefined) {
        setMinPrice(search.filters.minPrice);
      } else {
        setMinPrice(undefined);
      }
      if (search.filters.maxPrice !== undefined) {
        setMaxPrice(search.filters.maxPrice);
      } else {
        setMaxPrice(undefined);
      }
      if (search.filters.stockStatus) {
        setStockStatus(search.filters.stockStatus);
      } else {
        setStockStatus(StockStatus.ALL);
      }
    } else {
      // Reset filters if no filters in search
      setSelectedCategory('');
      setMinPrice(undefined);
      setMaxPrice(undefined);
      setStockStatus(StockStatus.ALL);
    }
  };

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
      {/* Header with Search and Filters */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 2,
          mb: 4,
          alignItems: 'center',
        }}
      >
        {/* Category Pills */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, flex: 1, minWidth: { xs: '100%', md: 'auto' } }}>
          <CategoryPill
            label="All Products"
            count={allProducts?.length || 0}
            isActive={selectedCategory === ''}
            onClick={() => setSelectedCategory('')}
          />
          {categoriesWithCounts.slice(0, 5).map((cat) => (
            <CategoryPill
              key={cat.name}
              label={cat.name}
              count={cat.count}
              isActive={selectedCategory === cat.name}
              onClick={() => setSelectedCategory(cat.name)}
            />
          ))}
        </Box>

        {/* Search and Filter Button */}
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', width: { xs: '100%', md: 'auto' } }}>
          <ProductSearch
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search products..."
          />
          <IconButton
            onClick={() => setFiltersOpen(true)}
            sx={{
              bgcolor: palette.sky,
              borderRadius: 2,
              '&:hover': { bgcolor: palette.primary, color: palette.surface },
            }}
          >
            <FilterListIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Recent Searches */}
      <RecentSearches onSearchSelect={handleRecentSearchSelect} limit={5} />

      {/* Filters Drawer */}
      <Drawer
        anchor="right"
        open={filtersOpen}
        onClose={() => setFiltersOpen(false)}
        sx={{
          '& .MuiDrawer-paper': {
            width: { xs: '100%', sm: 360 },
            p: 3,
          },
        }}
      >
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" fontWeight={700}>
            Filters
          </Typography>
        </Box>
        <ProductFilters
          categories={uniqueCategories}
          selectedCategory={selectedCategory}
          onCategoryChange={setSelectedCategory}
          minPrice={minPrice}
          maxPrice={maxPrice}
          onPriceChange={(min, max) => {
            setMinPrice(min);
            setMaxPrice(max);
          }}
          stockStatus={stockStatus}
          onStockStatusChange={setStockStatus}
        />
        <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
          <Button
            fullWidth
            variant="outlined"
            onClick={() => {
              setSelectedCategory('');
              setMinPrice(undefined);
              setMaxPrice(undefined);
              setStockStatus(StockStatus.ALL);
            }}
          >
            Clear All
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={() => {
              setShouldSaveSearch(true);
              setFiltersOpen(false);
            }}
          >
            Apply
          </Button>
        </Box>
      </Drawer>

      {/* Loading State */}
      {isLoading && (
        <Grid container spacing={3}>
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Grid item xs={12} sm={6} md={4} key={i}>
              <Skeleton variant="rounded" height={340} sx={{ borderRadius: 3 }} />
            </Grid>
          ))}
        </Grid>
      )}

      {/* Error State */}
      {error && (
        <Alert severity="error" sx={{ borderRadius: 2, mb: 4 }}>
          Failed to load products. Please try again later.
        </Alert>
      )}

      {/* Empty State */}
      {!isLoading && products?.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 8,
            bgcolor: palette.surface,
            borderRadius: 3,
            border: `1px solid ${palette.border}`,
          }}
        >
          <Inventory2OutlinedIcon sx={{ fontSize: 64, color: palette.textMuted, mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No products found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Try adjusting your search or filter criteria
          </Typography>
        </Box>
      )}

      {/* Products Grid */}
      <Grid container spacing={3}>
        {products?.map((product) => {
          const stockCount = getAvailableStock(product.id);
          const outOfStock = isProductOutOfStock(product.id);
          const isPreorderable = product.isPreorderable;

          return (
            <Grid item xs={12} sm={6} md={4} key={product.id}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  position: 'relative',
                  overflow: 'visible',
                  opacity: outOfStock && !isPreorderable ? 0.7 : 1,
                  border: outOfStock && !isPreorderable ? '2px solid' : '1px solid',
                  borderColor: outOfStock && !isPreorderable ? 'error.light' : 'divider',
                }}
              >
                {/* Stock Badge */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 12,
                    left: 12,
                    zIndex: 2,
                  }}
                >
                  {outOfStock ? (
                    <Chip
                      label={isPreorderable ? 'Pre-order' : 'Out of Stock'}
                      size="small"
                      color={isPreorderable ? 'warning' : 'error'}
                      sx={{
                        fontWeight: 700,
                        fontSize: '0.7rem',
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        bgcolor: palette.primary,
                        color: palette.textOnPrimary,
                        px: 1.5,
                        py: 0.5,
                        borderRadius: 50,
                        fontSize: '0.7rem',
                        fontWeight: 700,
                      }}
                    >
                      {stockCount} in Stock
                    </Box>
                  )}
                </Box>

                {/* Product Image */}
                <Box
                  component={RouterLink}
                  to={`/products/${product.id}`}
                  sx={{
                    height: 200,
                    bgcolor: palette.sky,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '16px 16px 0 0',
                    overflow: 'hidden',
                    position: 'relative',
                  }}
                >
                  {product.images && product.images.length > 0 ? (
                    <Box
                      component="img"
                      src={getImageUrl(product.images[0].thumbnail)}
                      alt={product.name}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain',
                        p: 3,
                        transition: 'transform 0.3s ease',
                        filter: outOfStock && !isPreorderable ? 'grayscale(50%)' : 'none',
                        '&:hover': { transform: 'scale(1.08)' },
                      }}
                    />
                  ) : (
                    <Inventory2OutlinedIcon sx={{ fontSize: 64, color: palette.textMuted }} />
                  )}
                </Box>

                {/* Product Info */}
                <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', pb: 1 }}>
                  <Typography
                    variant="subtitle1"
                    fontWeight={600}
                    gutterBottom
                    noWrap
                    component={RouterLink}
                    to={`/products/${product.id}`}
                    sx={{ textDecoration: 'none', color: 'inherit' }}
                  >
                    {product.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{
                      mb: 2,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      flex: 1,
                      lineHeight: 1.5,
                    }}
                  >
                    {product.description || 'No description available'}
                  </Typography>
                  <Typography variant="h6" fontWeight={700} color={palette.textPrimary}>
                    {typeof product.price === 'string'
                      ? parseFloat(product.price).toFixed(2)
                      : (product.price?.toFixed(2) || '0.00')} DZD
                  </Typography>
                </CardContent>

                {/* Add to Cart Button */}
                <Box sx={{ p: 2, pt: 0 }}>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<AddOutlinedIcon />}
                    onClick={() => handleAddToCart(product)}
                    disabled={outOfStock && !isPreorderable}
                    sx={{
                      borderColor: outOfStock && !isPreorderable ? 'error.light' : palette.border,
                      color: outOfStock && !isPreorderable ? 'error.main' : palette.textPrimary,
                      fontWeight: 500,
                      py: 1.25,
                      '&:hover': {
                        borderColor: palette.primary,
                        bgcolor: `${palette.primary}15`,
                      },
                      '&:disabled': {
                        borderColor: 'error.light',
                        color: 'error.main',
                        bgcolor: 'error.lighter',
                      },
                    }}
                  >
                    {outOfStock
                      ? isPreorderable
                        ? 'Pre-order'
                        : 'Out of Stock'
                      : 'Add to Cart'}
                  </Button>
                </Box>
              </Card>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );
};
